import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PilotOnboardingRelationshipSelection, PilotRelationshipAccessType } from '@shared';
import { toast } from 'sonner';

import { useDebounce } from '@/hooks/useDebounce';
import { fromDateOnlyString } from '@/shared/lib/utils';
import {
  pilotOnboardingApi,
  pilotOnboardingKeys,
  type PilotAdvisorIdentity,
  type PilotAdvisorSearchResult,
  type PilotOnboardingInput,
  type PilotOnboardingPreflight,
  type PilotOnboardingRecord,
  type PilotProgram,
  type PilotRelationshipIdentity,
} from './pilot-onboarding-api';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY_PROGRAMS: PilotProgram[] = [];
const EMPTY_ONBOARDINGS: PilotOnboardingRecord[] = [];

export type PilotOnboardingView = 'list' | 'form' | 'program-settings' | 'submitting' | 'details';
export type PilotOnboardingStatusFilter = 'all' | 'attention' | 'waiting' | 'complete';

export type SelectedPilotRelationship = {
  advisor: PilotRelationshipIdentity;
  accessType: PilotRelationshipAccessType;
  preRegisteredRelationshipIds: string[];
};

export interface PilotOnboardingActions {
  canRetry: boolean;
  canResend: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export function getPilotOnboardingActions(record: PilotOnboardingRecord): PilotOnboardingActions {
  const isManagedOnboarding = record.source !== 'legacy';
  const hasSeat = !!record.subscriptionSeatId;
  return {
    canRetry: isManagedOnboarding && record.status === 'needs_attention',
    canResend:
      isManagedOnboarding && hasSeat && ['invited', 'needs_attention'].includes(record.status),
    canEdit:
      isManagedOnboarding &&
      hasSeat &&
      ['invited', 'declined', 'expired', 'needs_attention'].includes(record.status),
    canDelete: isManagedOnboarding && !['active', 'deleted', 'processing'].includes(record.status),
  };
}

type PilotOnboardingFormSnapshot = {
  programId: string;
  roleKey: string;
  email: string;
  ownerAdvisorId: string | null;
  expiresDate: string;
  staff: SelectedPilotRelationship[];
  supervisors: SelectedPilotRelationship[];
};

function formSnapshotSignature(snapshot: PilotOnboardingFormSnapshot): string {
  return JSON.stringify({
    ...snapshot,
    staff: snapshot.staff.map(item => ({
      advisorEmail: normalizeEmail(item.advisor.advisorEmail),
      accessType: item.accessType,
    })),
    supervisors: snapshot.supervisors.map(item => ({
      advisorEmail: normalizeEmail(item.advisor.advisorEmail),
      accessType: item.accessType,
    })),
  });
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function pilotAdvisorName(advisor: PilotRelationshipIdentity | null | undefined): string {
  if (!advisor) return 'Unknown advisor';
  const name = [advisor.advisorFirstName, advisor.advisorLastName].filter(Boolean).join(' ').trim();
  return name || advisor.advisorEmail;
}

export function pilotAdvisorNameOrEmail(
  advisor: PilotRelationshipIdentity | null | undefined,
  email: string,
): string {
  return advisor ? pilotAdvisorName(advisor) : email;
}

export function formatPilotDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function advisorByEmail(
  program: PilotProgram,
  email: string,
): PilotAdvisorSearchResult | null {
  return (
    program.referencedAdvisors.find(
      advisor => normalizeEmail(advisor.advisorEmail) === normalizeEmail(email),
    ) ?? null
  );
}

export function advisorById(
  program: PilotProgram,
  advisorId: string | null | undefined,
): PilotAdvisorSearchResult | null {
  return program.referencedAdvisors.find(advisor => advisor.advisorId === advisorId) ?? null;
}

function configuredAdvisor(program: PilotProgram, email: string): PilotRelationshipIdentity {
  return (
    advisorByEmail(program, email) ?? {
      advisorId: null,
      advisorEmail: email,
      advisorFirstName: null,
      advisorLastName: null,
      organizationName: null,
      organizationDisplayName: null,
    }
  );
}

export function dateInputValue(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '';
}

export function dateTimeValue(value: string): string | null {
  return value ? `${value}T12:00:00.000Z` : null;
}

export function minimumFutureDate(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const day = String(tomorrow.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isFutureDateInput(value: string): boolean {
  const dateTime = dateTimeValue(value);
  return !!dateTime && new Date(dateTime).getTime() > Date.now();
}

function statusMatches(
  record: PilotOnboardingRecord,
  filter: PilotOnboardingStatusFilter,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'complete') return record.status === 'active';
  if (filter === 'waiting') return ['pending', 'processing', 'invited'].includes(record.status);
  return ['needs_attention', 'declined', 'expired'].includes(record.status);
}

export function usePilotOnboardingQueries(params: {
  view: PilotOnboardingView;
  programId: string;
  setProgramId: (programId: string) => void;
  email: string;
  editing: PilotOnboardingRecord | null;
  staffQuery: string;
}) {
  const programsQuery = useQuery({
    queryKey: pilotOnboardingKeys.programs(),
    queryFn: pilotOnboardingApi.fetchPrograms,
  });
  const onboardingsQuery = useQuery({
    queryKey: pilotOnboardingKeys.onboardings(),
    queryFn: pilotOnboardingApi.fetchOnboardings,
  });
  const programs = programsQuery.data ?? EMPTY_PROGRAMS;
  const onboardings = onboardingsQuery.data ?? EMPTY_ONBOARDINGS;

  useEffect(() => {
    if (params.programId || programs.length === 0) return;
    const preferred = programs.find(
      program => program.programKey === 'edward-jones' && program.isActive,
    );
    params.setProgramId(
      (preferred ?? programs.find(program => program.isActive) ?? programs[0]).id,
    );
  }, [params.programId, params.setProgramId, programs]);

  const normalizedEmail = normalizeEmail(params.email);
  const validEmail = EMAIL_PATTERN.test(normalizedEmail);
  const debouncedEmail = useDebounce(normalizedEmail, 2_000);
  const sameEditingIdentity =
    !!params.editing &&
    normalizedEmail === normalizeEmail(params.editing.email) &&
    params.programId === params.editing.pilotProgramId;
  const preflightQuery = useQuery({
    queryKey: pilotOnboardingKeys.preflight(params.programId, debouncedEmail),
    queryFn: () => pilotOnboardingApi.preflight(debouncedEmail, params.programId),
    enabled:
      params.view === 'form' &&
      !!params.programId &&
      validEmail &&
      debouncedEmail === normalizedEmail &&
      !sameEditingIdentity,
    retry: false,
  });
  const currentPreflight =
    debouncedEmail === normalizedEmail && preflightQuery.data?.email === normalizedEmail
      ? preflightQuery.data
      : null;

  const debouncedStaffQuery = useDebounce(params.staffQuery.trim(), 300);
  const staffSearchQuery = useQuery({
    queryKey: pilotOnboardingKeys.advisorSearch('relationship', debouncedStaffQuery),
    queryFn: () => pilotOnboardingApi.searchAdvisors(debouncedStaffQuery, 'relationship'),
    enabled: params.view === 'form' && debouncedStaffQuery.length >= 2,
    retry: false,
  });

  return {
    programsQuery,
    onboardingsQuery,
    programs,
    onboardings,
    normalizedEmail,
    validEmail,
    debouncedEmail,
    sameEditingIdentity,
    preflightQuery,
    currentPreflight,
    preflightReady: sameEditingIdentity || !!currentPreflight?.eligible,
    debouncedStaffQuery,
    staffSearchQuery,
  };
}

export function usePilotOnboardingMutations(params: {
  editing: PilotOnboardingRecord | null;
  lastResult: PilotOnboardingRecord | null;
  selectedRecord: PilotOnboardingRecord | null;
  setLastResult: Dispatch<SetStateAction<PilotOnboardingRecord | null>>;
  setSelectedRecord: Dispatch<SetStateAction<PilotOnboardingRecord | null>>;
  setReviewOpen: Dispatch<SetStateAction<boolean>>;
  setView: Dispatch<SetStateAction<PilotOnboardingView>>;
  setEditing: Dispatch<SetStateAction<PilotOnboardingRecord | null>>;
  setPendingDelete: Dispatch<SetStateAction<PilotOnboardingRecord | null>>;
}) {
  const queryClient = useQueryClient();
  const invalidateOnboardings = () =>
    queryClient.invalidateQueries({ queryKey: pilotOnboardingKeys.onboardings() });

  const submitMutation = useMutation({
    mutationFn: (input: PilotOnboardingInput) =>
      params.editing
        ? pilotOnboardingApi.update(params.editing.id, input)
        : pilotOnboardingApi.create(input),
    onSuccess: result => {
      if (result.status !== 'needs_attention') {
        toast.success(params.editing ? 'Pilot invitation updated' : 'Pilot invitation created');
      }
      params.setLastResult(result);
      params.setSelectedRecord(result);
      params.setReviewOpen(false);
      params.setView('details');
      params.setEditing(null);
      invalidateOnboardings();
    },
    onError: () => {
      if (!params.editing) {
        params.setView('form');
        params.setReviewOpen(true);
      }
    },
  });

  const retryMutation = useMutation({
    mutationFn: (pilotOnboardingId: string) => pilotOnboardingApi.retry(pilotOnboardingId),
    onSuccess: result => {
      toast.success(
        result.status === 'needs_attention'
          ? 'Retry finished; one step still needs attention'
          : 'Pilot onboarding retried',
      );
      params.setLastResult(result);
      params.setSelectedRecord(current => (current?.id === result.id ? result : current));
      invalidateOnboardings();
    },
  });

  const resendMutation = useMutation({
    mutationFn: (pilotOnboardingId: string) => pilotOnboardingApi.resend(pilotOnboardingId),
    onSuccess: result => {
      toast.success('Invitation resent');
      params.setLastResult(result);
      params.setSelectedRecord(current => (current?.id === result.id ? result : current));
      invalidateOnboardings();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (pilotOnboardingId: string) => pilotOnboardingApi.delete(pilotOnboardingId),
    onSuccess: result => {
      toast.success(
        result.retainedAppliedAccess
          ? 'Invitation deleted; existing account access was preserved'
          : 'Invitation and pre-registration setup deleted',
      );
      params.setPendingDelete(null);
      if (params.lastResult?.id === result.id) params.setLastResult(null);
      if (params.selectedRecord?.id === result.id) {
        params.setSelectedRecord(null);
        params.setView('list');
      }
      invalidateOnboardings();
    },
  });

  return { submitMutation, retryMutation, resendMutation, deleteMutation };
}

export function usePilotOnboardingAdminController() {
  const queryClient = useQueryClient();
  const hydratedPreflightRelationships = useRef('');
  const [view, setView] = useState<PilotOnboardingView>('list');
  const [editing, setEditing] = useState<PilotOnboardingRecord | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<PilotOnboardingRecord | null>(null);
  const [lastResult, setLastResult] = useState<PilotOnboardingRecord | null>(null);
  const [programId, setProgramId] = useState('');
  const [roleKey, setRoleKey] = useState('');
  const [email, setEmail] = useState('');
  const [owner, setOwner] = useState<PilotAdvisorSearchResult | null>(null);
  const [expiresDate, setExpiresDate] = useState('');
  const [staff, setStaff] = useState<SelectedPilotRelationship[]>([]);
  const [supervisors, setSupervisors] = useState<SelectedPilotRelationship[]>([]);
  const [staffQuery, setStaffQuery] = useState('');
  const [staffExpanded, setStaffExpanded] = useState(true);
  const [supervisorsExpanded, setSupervisorsExpanded] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [expiryCalendarOpen, setExpiryCalendarOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PilotOnboardingRecord | null>(null);
  const [pendingStaffRemoval, setPendingStaffRemoval] = useState<SelectedPilotRelationship | null>(
    null,
  );
  const [initialFormSignature, setInitialFormSignature] = useState<string | null>(null);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [formWasDirtyBeforeSettings, setFormWasDirtyBeforeSettings] = useState(false);
  const [listQuery, setListQuery] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<PilotOnboardingStatusFilter>('all');

  const queryState = usePilotOnboardingQueries({
    view,
    programId,
    setProgramId,
    email,
    editing,
    staffQuery,
  });

  useEffect(() => {
    if (!queryState.currentPreflight) return;
    const relationships = queryState.currentPreflight.preRegisteredRelationships ?? [];
    const hydrationSignature = JSON.stringify({
      email: queryState.currentPreflight.email,
      relationships: relationships.map(relationship => ({
        email: relationship.relatedAdvisorEmail,
        accessType: relationship.accessType,
        ids: relationship.preRegisteredRelationshipIds,
      })),
    });
    if (hydratedPreflightRelationships.current === hydrationSignature) return;
    hydratedPreflightRelationships.current = hydrationSignature;
    const preRegisteredStaff = relationships.map(relationship => ({
      advisor:
        relationship.relatedAdvisor ??
        ({
          advisorId: null,
          advisorEmail: relationship.relatedAdvisorEmail,
          advisorFirstName: null,
          advisorLastName: null,
          organizationName: null,
          organizationDisplayName: null,
        } satisfies PilotRelationshipIdentity),
      accessType: relationship.accessType,
      preRegisteredRelationshipIds: relationship.preRegisteredRelationshipIds,
    }));
    setStaff(current => {
      const manuallySelected = current.filter(
        item => item.preRegisteredRelationshipIds.length === 0,
      );
      const incomingEmails = new Set(
        preRegisteredStaff.map(item => normalizeEmail(item.advisor.advisorEmail)),
      );
      return [
        ...manuallySelected.filter(
          item => !incomingEmails.has(normalizeEmail(item.advisor.advisorEmail)),
        ),
        ...preRegisteredStaff,
      ];
    });
  }, [editing, queryState.currentPreflight]);

  const removeStaffMutation = useMutation({
    mutationFn: async (relationship: SelectedPilotRelationship) => {
      if (relationship.preRegisteredRelationshipIds.length === 0) return;
      await pilotOnboardingApi.deletePreRegisteredRelationships({
        email: queryState.normalizedEmail,
        relationshipIds: relationship.preRegisteredRelationshipIds,
      });
    },
    onSuccess: (_, relationship) => {
      const removedEmail = normalizeEmail(relationship.advisor.advisorEmail);
      setStaff(current =>
        current.filter(item => normalizeEmail(item.advisor.advisorEmail) !== removedEmail),
      );
      queryClient.setQueryData<PilotOnboardingPreflight>(
        pilotOnboardingKeys.preflight(programId, queryState.normalizedEmail),
        current =>
          current
            ? {
                ...current,
                preRegisteredRelationships: (current.preRegisteredRelationships ?? []).filter(
                  item => normalizeEmail(item.relatedAdvisorEmail) !== removedEmail,
                ),
              }
            : current,
      );
      setPendingStaffRemoval(null);
    },
  });
  const { programs, onboardings } = queryState;
  const selectedProgram = programs.find(program => program.id === programId) ?? null;
  const selectedRole = selectedProgram?.configuration.roles.find(
    role => role.key === roleKey && role.isActive,
  );
  const activeSupervisors = supervisors;
  const supervisorAccessSummary = activeSupervisors.every(
    supervisor => supervisor.accessType === 'no_client_access',
  )
    ? 'No client access'
    : 'Mixed access';
  const supervisorSummary = activeSupervisors.length
    ? `${activeSupervisors
        .slice(0, 2)
        .map(supervisor => pilotAdvisorName(supervisor.advisor))
        .join(
          ', ',
        )}${activeSupervisors.length > 2 ? ` +${activeSupervisors.length - 2}` : ''} · ${supervisorAccessSummary}`
    : 'No supervisors';
  const staffSummary = staff.length
    ? `${staff.length} ${staff.length === 1 ? 'team member' : 'team members'}`
    : 'No staff added';
  const activeRoles = selectedProgram?.configuration.roles.filter(role => role.isActive) ?? [];
  const selectedExpiryDate = fromDateOnlyString(expiresDate);
  const earliestExpiryDate = fromDateOnlyString(minimumFutureDate())!;
  const supervisorEmails = new Set(
    supervisors.map(item => normalizeEmail(item.advisor.advisorEmail)),
  );
  const visibleStaffResults = (queryState.staffSearchQuery.data ?? []).filter(
    advisor =>
      normalizeEmail(advisor.advisorEmail) !== queryState.normalizedEmail &&
      !supervisorEmails.has(normalizeEmail(advisor.advisorEmail)) &&
      !staff.some(
        item => normalizeEmail(item.advisor.advisorEmail) === normalizeEmail(advisor.advisorEmail),
      ),
  );
  const normalizedStaffQuery = normalizeEmail(staffQuery);
  const canAddStaffEmail =
    EMAIL_PATTERN.test(normalizedStaffQuery) &&
    normalizedStaffQuery !== queryState.normalizedEmail &&
    !supervisorEmails.has(normalizedStaffQuery) &&
    !staff.some(item => normalizeEmail(item.advisor.advisorEmail) === normalizedStaffQuery) &&
    !(queryState.staffSearchQuery.data ?? []).some(
      advisor => normalizeEmail(advisor.advisorEmail) === normalizedStaffQuery,
    );
  const detailRecord = selectedRecord;
  const detailProgram = detailRecord
    ? (programs.find(program => program.id === detailRecord.pilotProgramId) ?? null)
    : null;
  const detailRole = detailProgram?.configuration.roles.find(
    role => role.key === detailRecord?.roleKey,
  );
  const detailOwner = detailRecord
    ? (detailRecord.subscriptionOwner ??
      (detailProgram ? advisorById(detailProgram, detailRecord.subscriptionOwnerAdvisorId) : null))
    : null;
  const detailOwnerName = detailOwner
    ? pilotAdvisorName(detailOwner)
    : detailRole
      ? detailRole.defaultSubscriptionOwnerEmail
      : 'the subscription owner';
  const detailActions = detailRecord ? getPilotOnboardingActions(detailRecord) : null;
  const detailRelationships = detailRecord?.requestSnapshot?.relationships ?? [];
  const detailSupervisorCount = detailRelationships.filter(
    relationship => relationship.kind === 'default_supervisor',
  ).length;
  const detailStaffCount = detailRelationships.filter(
    relationship => relationship.kind === 'associated_staff',
  ).length;
  const ownerDisplayName = selectedRole
    ? pilotAdvisorNameOrEmail(owner, selectedRole.defaultSubscriptionOwnerEmail)
    : owner
      ? pilotAdvisorName(owner)
      : 'No owner selected';
  const reviewDefaultExpiresDate = selectedProgram
    ? dateInputValue(selectedProgram.configuration.defaultSeatExpiresDateTime)
    : '';
  const reviewExpiryIsCustom =
    !!reviewDefaultExpiresDate && expiresDate !== reviewDefaultExpiresDate;
  const reviewExpiryNote = reviewExpiryIsCustom
    ? `Custom date · pilot ends ${formatPilotDate(dateTimeValue(reviewDefaultExpiresDate))}`
    : 'Pilot end date';
  const currentFormSignature = formSnapshotSignature({
    programId,
    roleKey,
    email,
    ownerAdvisorId: owner?.advisorId ?? null,
    expiresDate,
    staff,
    supervisors,
  });
  const hasUnsavedFormChanges =
    initialFormSignature !== null && currentFormSignature !== initialFormSignature;

  const mutations = usePilotOnboardingMutations({
    editing,
    lastResult,
    selectedRecord,
    setLastResult,
    setSelectedRecord,
    setReviewOpen,
    setView,
    setEditing,
    setPendingDelete,
  });

  function setProgramDefaults(program: PilotProgram, requestedRoleKey?: string) {
    const activeRoles = program.configuration.roles.filter(role => role.isActive);
    const role =
      activeRoles.find(candidate => candidate.key === requestedRoleKey) ?? activeRoles[0] ?? null;
    const defaultOwner = role ? advisorByEmail(program, role.defaultSubscriptionOwnerEmail) : null;
    const defaultExpiresDate = dateInputValue(program.configuration.defaultSeatExpiresDateTime);
    const defaultSupervisors = program.configuration.defaultSupervisors
      .filter(supervisor => supervisor.isActive)
      .map(supervisor => ({
        advisor: configuredAdvisor(program, supervisor.email),
        accessType: supervisor.accessType,
        preRegisteredRelationshipIds: [],
      }));
    setProgramId(program.id);
    setRoleKey(role?.key ?? '');
    setOwner(defaultOwner);
    setExpiresDate(defaultExpiresDate);
    setStaff([]);
    setStaffQuery('');
    setSupervisors(defaultSupervisors);
    setExpiryCalendarOpen(false);

    return formSnapshotSignature({
      programId: program.id,
      roleKey: role?.key ?? '',
      email: '',
      ownerAdvisorId: defaultOwner?.advisorId ?? null,
      expiresDate: defaultExpiresDate,
      staff: [],
      supervisors: defaultSupervisors,
    });
  }

  function startCreate() {
    const program =
      programs.find(candidate => candidate.id === programId && candidate.isActive) ??
      programs.find(candidate => candidate.isActive);
    if (!program) return;
    hydratedPreflightRelationships.current = '';
    setEditing(null);
    setLastResult(null);
    setSelectedRecord(null);
    setEmail('');
    setStaffExpanded(true);
    setSupervisorsExpanded(false);
    setInitialFormSignature(setProgramDefaults(program));
    setShowUnsavedChangesDialog(false);
    setView('form');
  }

  function startEdit(record: PilotOnboardingRecord) {
    const program = programs.find(candidate => candidate.id === record.pilotProgramId);
    if (!program || record.source === 'legacy') return;
    hydratedPreflightRelationships.current = '';
    setEditing(record);
    setLastResult(null);
    setSelectedRecord(null);
    setProgramId(program.id);
    const nextRoleKey =
      record.roleKey ?? program.configuration.roles.find(role => role.isActive)?.key ?? '';
    const nextOwner = advisorById(program, record.subscriptionOwnerAdvisorId);
    const nextExpiresDate = dateInputValue(record.seatExpiresDateTime);
    setRoleKey(nextRoleKey);
    setEmail(record.email);
    setOwner(nextOwner);
    setExpiresDate(nextExpiresDate);
    setStaffQuery('');
    setStaffExpanded(true);
    setSupervisorsExpanded(false);
    setExpiryCalendarOpen(false);

    const snapshotRelationships = record.requestSnapshot?.relationships ?? [];
    const defaultSupervisors = program.configuration.defaultSupervisors
      .filter(supervisor => supervisor.isActive)
      .map(supervisor => ({
        advisor: configuredAdvisor(program, supervisor.email),
        accessType: supervisor.accessType,
        preRegisteredRelationshipIds: [],
      }));
    const snapshotSupervisors = snapshotRelationships
      .filter(relationship => relationship.kind === 'default_supervisor')
      .map(relationship => ({
        advisor:
          advisorById(program, relationship.relatedAdvisorId) ??
          ({
            advisorId: relationship.relatedAdvisorId,
            advisorEmail: relationship.relatedAdvisorEmail,
            advisorFirstName: null,
            advisorLastName: null,
            organizationName: null,
            organizationDisplayName: null,
          } satisfies PilotRelationshipIdentity),
        accessType: relationship.accessType,
        preRegisteredRelationshipIds: [],
      }));
    const nextSupervisors = record.requestSnapshot ? snapshotSupervisors : defaultSupervisors;
    const nextStaff = snapshotRelationships
      .filter(relationship => relationship.kind === 'associated_staff')
      .map(relationship => ({
        advisor:
          advisorById(program, relationship.relatedAdvisorId) ??
          ({
            advisorId: relationship.relatedAdvisorId,
            advisorEmail: relationship.relatedAdvisorEmail,
            advisorFirstName: null,
            advisorLastName: null,
            organizationName: null,
            organizationDisplayName: null,
          } satisfies PilotRelationshipIdentity),
        accessType: relationship.accessType,
        preRegisteredRelationshipIds: [],
      }));
    setSupervisors(nextSupervisors);
    setStaff(nextStaff);
    setInitialFormSignature(
      formSnapshotSignature({
        programId: program.id,
        roleKey: nextRoleKey,
        email: record.email,
        ownerAdvisorId: nextOwner?.advisorId ?? null,
        expiresDate: nextExpiresDate,
        staff: nextStaff,
        supervisors: nextSupervisors,
      }),
    );
    setShowUnsavedChangesDialog(false);
    setView('form');
  }

  function leaveForm() {
    setShowUnsavedChangesDialog(false);
    setExpiryCalendarOpen(false);
    setInitialFormSignature(null);
    setView('list');
    setEditing(null);
  }

  function requestLeaveForm() {
    if (hasUnsavedFormChanges) {
      setShowUnsavedChangesDialog(true);
      return;
    }
    leaveForm();
  }

  function openPartnerSettings() {
    if (!selectedProgram || editing) return;
    setFormWasDirtyBeforeSettings(hasUnsavedFormChanges);
    setView('program-settings');
  }

  function backFromPartnerSettings() {
    setView('form');
  }

  function applyProgramSettings(updatedProgram: PilotProgram) {
    const nextActiveRoles = updatedProgram.configuration.roles.filter(role => role.isActive);
    const nextRole = nextActiveRoles[0] ?? null;
    const nextOwner = nextRole
      ? advisorByEmail(updatedProgram, nextRole.defaultSubscriptionOwnerEmail)
      : null;
    const nextSupervisors = updatedProgram.configuration.defaultSupervisors
      .filter(supervisor => supervisor.isActive)
      .map(supervisor => ({
        advisor: configuredAdvisor(updatedProgram, supervisor.email),
        accessType: supervisor.accessType,
        preRegisteredRelationshipIds: [],
      }));
    const nextExpiresDate = dateInputValue(updatedProgram.configuration.defaultSeatExpiresDateTime);
    setRoleKey(nextRole?.key ?? '');
    setOwner(nextOwner);
    setSupervisors(nextSupervisors);
    setExpiresDate(nextExpiresDate);
    if (!formWasDirtyBeforeSettings) {
      setInitialFormSignature(
        formSnapshotSignature({
          programId: updatedProgram.id,
          roleKey: nextRole?.key ?? '',
          email,
          ownerAdvisorId: nextOwner?.advisorId ?? null,
          expiresDate: nextExpiresDate,
          staff,
          supervisors: nextSupervisors,
        }),
      );
    }
    setFormWasDirtyBeforeSettings(false);
    setView('form');
  }

  function changeProgram(nextProgramId: string) {
    const program = programs.find(candidate => candidate.id === nextProgramId);
    if (program) setProgramDefaults(program);
  }

  function changeRole(nextRoleKey: string) {
    if (!selectedProgram) return;
    const role = selectedProgram.configuration.roles.find(
      candidate => candidate.key === nextRoleKey,
    );
    setRoleKey(nextRoleKey);
    setOwner(role ? advisorByEmail(selectedProgram, role.defaultSubscriptionOwnerEmail) : null);
  }

  function changeEmail(nextEmail: string) {
    if (normalizeEmail(nextEmail) !== normalizeEmail(email)) {
      hydratedPreflightRelationships.current = '';
      setStaff([]);
      setPendingStaffRemoval(null);
    }
    setEmail(nextEmail);
  }

  function addStaff(advisor: PilotAdvisorIdentity) {
    setStaff(current =>
      current.some(
        item => normalizeEmail(item.advisor.advisorEmail) === normalizeEmail(advisor.advisorEmail),
      )
        ? current
        : [...current, { advisor, accessType: 'two_way', preRegisteredRelationshipIds: [] }],
    );
    setStaffQuery('');
  }

  function addStaffEmail() {
    if (!canAddStaffEmail) return;
    setStaff(current => [
      ...current,
      {
        advisor: {
          advisorId: null,
          advisorEmail: normalizedStaffQuery,
          advisorFirstName: null,
          advisorLastName: null,
          organizationName: null,
          organizationDisplayName: null,
        },
        accessType: 'two_way',
        preRegisteredRelationshipIds: [],
      },
    ]);
    setStaffQuery('');
  }

  function buildRelationships(): PilotOnboardingRelationshipSelection[] {
    return [
      ...supervisors.map(item => ({
        relatedAdvisorId: item.advisor.advisorId,
        relatedAdvisorEmail: normalizeEmail(item.advisor.advisorEmail),
        kind: 'default_supervisor' as const,
        accessType: item.accessType,
      })),
      ...staff.map(item => ({
        relatedAdvisorId: item.advisor.advisorId,
        relatedAdvisorEmail: normalizeEmail(item.advisor.advisorEmail),
        kind: 'associated_staff' as const,
        accessType: item.accessType,
      })),
    ];
  }

  function submitFromReview() {
    if (!selectedProgram || !selectedRole || !expiresDate) return;
    const input: PilotOnboardingInput = {
      pilotProgramId: selectedProgram.id,
      email: queryState.normalizedEmail,
      roleKey: selectedRole.key,
      seatExpiresDateTime: dateTimeValue(expiresDate),
      relationships: buildRelationships(),
    };
    if (!editing) {
      setReviewOpen(false);
      setView('submitting');
    }
    mutations.submitMutation.mutate(input);
  }

  const hasMissingDefaultSupervisorAccount =
    selectedProgram?.configuration.defaultSupervisors.some(
      supervisor => supervisor.isActive && !advisorByEmail(selectedProgram, supervisor.email),
    ) ?? false;
  const programDefaultsError = hasMissingDefaultSupervisorAccount
    ? 'Pilot program default supervisors must have Waterlily accounts.'
    : selectedRole && !owner
      ? 'Default subscription owner not found.'
      : owner && !owner.ownsActiveSubscription
        ? 'Subscription owner must own an active subscription.'
        : null;
  const canSubmit =
    !!selectedProgram &&
    !!selectedRole &&
    !programDefaultsError &&
    isFutureDateInput(expiresDate) &&
    queryState.validEmail &&
    queryState.preflightReady &&
    !mutations.submitMutation.isPending;
  const isRelationshipLookupPending =
    view === 'form' &&
    !!programId &&
    queryState.validEmail &&
    !queryState.sameEditingIdentity &&
    (queryState.debouncedEmail !== queryState.normalizedEmail ||
      queryState.preflightQuery.isFetching);

  const preflightMessage = useMemo(() => {
    if (!email.trim()) {
      return { text: 'Enter the pilot user’s email address.', className: 'text-muted-foreground' };
    }
    if (!queryState.validEmail) {
      return { text: 'Enter a valid email address.', className: 'text-red-600' };
    }
    if (queryState.sameEditingIdentity) {
      return { text: 'Editing this existing invitation.', className: 'text-primary-dark' };
    }
    if (
      queryState.debouncedEmail !== queryState.normalizedEmail ||
      queryState.preflightQuery.isFetching
    ) {
      return { text: '', className: 'text-muted-foreground' };
    }
    if (queryState.preflightQuery.isError) {
      return { text: 'Could not verify this email. Try again.', className: 'text-red-600' };
    }
    if (!queryState.currentPreflight) {
      return { text: '', className: 'text-muted-foreground' };
    }
    if (!queryState.currentPreflight.eligible) {
      const labelByBlocker: Record<(typeof queryState.currentPreflight.blockers)[number], string> =
        {
          pending_invitation: 'This email already has a pending seat invitation.',
          active_seat: 'This user already has an active subscription seat.',
          owned_subscription: 'This user already owns a subscription.',
          open_onboarding: 'This email already has an open pilot onboarding.',
          legacy_pilot_user: 'This person is already in the legacy pilot.',
        };
      return {
        text: queryState.currentPreflight.blockers
          .map(blocker => labelByBlocker[blocker])
          .join(' '),
        className: 'text-red-600',
      };
    }
    return queryState.currentPreflight.advisorExists
      ? {
          text: 'Waterlily account found. Pilot setup will apply immediately.',
          className: 'text-green-700',
        }
      : {
          text: 'No Waterlily account yet. Flags and relationships apply when they sign up.',
          className: 'text-green-700',
        };
  }, [
    email,
    queryState.currentPreflight,
    queryState.debouncedEmail,
    queryState.normalizedEmail,
    queryState.preflightQuery.isError,
    queryState.preflightQuery.isFetching,
    queryState.sameEditingIdentity,
    queryState.validEmail,
  ]);

  const filteredOnboardings = useMemo(() => {
    const search = listQuery.trim().toLowerCase();
    return onboardings.filter(record => {
      if (partnerFilter !== 'all' && record.pilotProgramId !== partnerFilter) return false;
      if (roleFilter !== 'all' && record.roleKey !== roleFilter) return false;
      if (!statusMatches(record, statusFilter)) return false;
      return !search || record.email.toLowerCase().includes(search);
    });
  }, [listQuery, onboardings, partnerFilter, roleFilter, statusFilter]);

  function changeListQuery(value: string) {
    setListQuery(value);
  }

  function changePartnerFilter(value: string) {
    setPartnerFilter(value);
    setRoleFilter('all');
  }

  function changeRoleFilter(value: string) {
    setRoleFilter(value);
  }

  function changeStatusFilter(value: PilotOnboardingStatusFilter) {
    setStatusFilter(value);
  }

  const statusCounts = useMemo(
    () => ({
      all: onboardings.length,
      attention: onboardings.filter(record => statusMatches(record, 'attention')).length,
      waiting: onboardings.filter(record => statusMatches(record, 'waiting')).length,
      complete: onboardings.filter(record => statusMatches(record, 'complete')).length,
    }),
    [onboardings],
  );

  return {
    ...queryState,
    ...mutations,
    view,
    setView,
    editing,
    selectedRecord,
    setSelectedRecord,
    lastResult,
    programId,
    roleKey,
    email,
    changeEmail,
    owner,
    expiresDate,
    setExpiresDate,
    staff,
    setStaff,
    supervisors,
    setSupervisors,
    staffQuery,
    setStaffQuery,
    staffExpanded,
    setStaffExpanded,
    supervisorsExpanded,
    setSupervisorsExpanded,
    reviewOpen,
    setReviewOpen,
    expiryCalendarOpen,
    setExpiryCalendarOpen,
    pendingDelete,
    setPendingDelete,
    pendingStaffRemoval,
    setPendingStaffRemoval,
    removeStaffMutation,
    showUnsavedChangesDialog,
    setShowUnsavedChangesDialog,
    listQuery,
    changeListQuery,
    partnerFilter,
    changePartnerFilter,
    roleFilter,
    changeRoleFilter,
    statusFilter,
    changeStatusFilter,
    selectedProgram,
    selectedRole,
    activeSupervisors,
    supervisorSummary,
    staffSummary,
    activeRoles,
    selectedExpiryDate,
    earliestExpiryDate,
    visibleStaffResults,
    canAddStaffEmail,
    normalizedStaffQuery,
    detailRecord,
    detailProgram,
    detailRole,
    detailOwnerName,
    detailActions,
    detailRelationships,
    detailSupervisorCount,
    detailStaffCount,
    ownerDisplayName,
    reviewExpiryIsCustom,
    reviewExpiryNote,
    startCreate,
    startEdit,
    leaveForm,
    requestLeaveForm,
    openPartnerSettings,
    backFromPartnerSettings,
    applyProgramSettings,
    changeProgram,
    changeRole,
    addStaff,
    addStaffEmail,
    submitFromReview,
    programDefaultsError,
    canSubmit,
    isRelationshipLookupPending,
    preflightMessage,
    filteredOnboardings,
    statusCounts,
  };
}
